import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { PlusCircle, Trash2, Save, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { QuoteComparisonBaseline } from '@/types/quotes-enhanced.types';

interface BaselineComparisonSetupProps {
  applicationId: string;
  onBaselineSet: (baseline: QuoteComparisonBaseline) => void;
  existingBaseline?: QuoteComparisonBaseline;
}

interface CostBreakdownItem {
  category: string;
  description: string;
  annual_cost: number;
}

interface ServiceGap {
  service: string;
  importance: 'high' | 'medium' | 'low';
  notes: string;
}

export const BaselineComparisonSetup: React.FC<BaselineComparisonSetupProps> = ({
  applicationId,
  onBaselineSet,
  existingBaseline
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(!existingBaseline);
  
  const [currentProvider, setCurrentProvider] = useState(
    existingBaseline?.current_provider_name || ''
  );
  const [totalAnnualCost, setTotalAnnualCost] = useState(
    existingBaseline?.total_annual_cost || 0
  );
  const [satisfactionRating, setSatisfactionRating] = useState(
    existingBaseline?.satisfaction_rating || 5
  );
  
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdownItem[]>(
    existingBaseline?.cost_breakdown || [
      { category: 'Payroll Processing', description: '', annual_cost: 0 },
      { category: 'Benefits Administration', description: '', annual_cost: 0 },
      { category: 'HR Support', description: '', annual_cost: 0 },
      { category: 'Workers Compensation', description: '', annual_cost: 0 },
      { category: 'Employment Practices Liability', description: '', annual_cost: 0 }
    ]
  );
  
  const [serviceGaps, setServiceGaps] = useState<ServiceGap[]>(
    existingBaseline?.service_gaps || []
  );

  const addCostItem = () => {
    setCostBreakdown([...costBreakdown, { category: '', description: '', annual_cost: 0 }]);
  };

  const removeCostItem = (index: number) => {
    setCostBreakdown(costBreakdown.filter((_, i) => i !== index));
  };

  const updateCostItem = (index: number, field: keyof CostBreakdownItem, value: any) => {
    const updated = [...costBreakdown];
    updated[index] = { ...updated[index], [field]: value };
    setCostBreakdown(updated);
  };

  const addServiceGap = () => {
    setServiceGaps([...serviceGaps, { service: '', importance: 'medium', notes: '' }]);
  };

  const removeServiceGap = (index: number) => {
    setServiceGaps(serviceGaps.filter((_, i) => i !== index));
  };

  const updateServiceGap = (index: number, field: keyof ServiceGap, value: any) => {
    const updated = [...serviceGaps];
    updated[index] = { ...updated[index], [field]: value };
    setServiceGaps(updated);
  };

  const calculateTotalFromBreakdown = () => {
    const total = costBreakdown.reduce((sum, item) => sum + (item.annual_cost || 0), 0);
    setTotalAnnualCost(total);
  };

  const handleSave = async () => {
    if (!applicationId) {
      toast.error('Application ID not found');
      return;
    }

    setIsLoading(true);
    try {
      const baselineData = {
        application_id: applicationId,
        current_provider_name: currentProvider,
        total_annual_cost: totalAnnualCost,
        cost_breakdown: costBreakdown as any,
        service_gaps: serviceGaps as any,
        satisfaction_rating: satisfactionRating
      };

      const { data, error } = existingBaseline
        ? await supabase
            .from('quote_comparison_baselines')
            .update(baselineData)
            .eq('id', existingBaseline.id)
            .select()
            .single()
        : await supabase
            .from('quote_comparison_baselines')
            .insert(baselineData)
            .select()
            .single();

      if (error) throw error;

      onBaselineSet(data);
      toast.success('Baseline comparison data saved successfully');
    } catch (error) {
      console.error('Error saving baseline:', error);
      toast.error('Failed to save baseline data');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {showGuide && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800">Setting Up Your Baseline</CardTitle>
            <CardDescription className="text-blue-600">
              To provide accurate quote comparisons, we need to understand your current costs and situation. 
              This helps us calculate potential savings and identify the best PEO options for your business.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-700">
                Don't worry if you don't have exact numbers - estimates work too. You can always update this later.
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowGuide(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                Got it
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Current Provider Information
          </CardTitle>
          <CardDescription>
            Help us understand your current situation to provide accurate comparisons
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="currentProvider">Current Provider Name</Label>
              <Input
                id="currentProvider"
                value={currentProvider}
                onChange={(e) => setCurrentProvider(e.target.value)}
                placeholder="Enter current provider name"
              />
            </div>
            <div>
              <Label htmlFor="totalCost">Total Annual Cost</Label>
              <Input
                id="totalCost"
                type="number"
                value={totalAnnualCost}
                onChange={(e) => setTotalAnnualCost(Number(e.target.value))}
                placeholder="0"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="satisfaction">Current Satisfaction Rating (1-10)</Label>
            <Select value={satisfactionRating.toString()} onValueChange={(value) => setSatisfactionRating(Number(value))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                  <SelectItem key={rating} value={rating.toString()}>
                    {rating} - {rating <= 3 ? 'Poor' : rating <= 6 ? 'Fair' : rating <= 8 ? 'Good' : 'Excellent'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Cost Breakdown
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={calculateTotalFromBreakdown}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Total
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={addCostItem}
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Break down your current costs by category for accurate comparison
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {costBreakdown.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg">
                <div>
                  <Label>Category</Label>
                  <Input
                    value={item.category}
                    onChange={(e) => updateCostItem(index, 'category', e.target.value)}
                    placeholder="Cost category"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateCostItem(index, 'description', e.target.value)}
                    placeholder="Service description"
                  />
                </div>
                <div>
                  <Label>Annual Cost</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={item.annual_cost}
                      onChange={(e) => updateCostItem(index, 'annual_cost', Number(e.target.value))}
                      placeholder="0"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeCostItem(index)}
                      className="px-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Service Gaps & Needs
            <Button
              variant="outline"
              size="sm"
              onClick={addServiceGap}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Gap
            </Button>
          </CardTitle>
          <CardDescription>
            Identify services you need but don't currently have
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {serviceGaps.map((gap, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg">
                <div>
                  <Label>Service Needed</Label>
                  <Input
                    value={gap.service}
                    onChange={(e) => updateServiceGap(index, 'service', e.target.value)}
                    placeholder="Service name"
                  />
                </div>
                <div>
                  <Label>Importance</Label>
                  <Select
                    value={gap.importance}
                    onValueChange={(value) => updateServiceGap(index, 'importance', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High Priority</SelectItem>
                      <SelectItem value="medium">Medium Priority</SelectItem>
                      <SelectItem value="low">Low Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={gap.notes}
                    onChange={(e) => updateServiceGap(index, 'notes', e.target.value)}
                    placeholder="Additional details"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeServiceGap(index)}
                    className="px-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {serviceGaps.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                No service gaps identified. Click "Add Gap" to identify services you need.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isLoading}>
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Baseline'}
        </Button>
      </div>
    </div>
  );
};